import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, Power } from 'lucide-react';
import type { AppIdEmUsoDetalhe } from '@/services/api/connections';

/**
 * O que aparece quando o app_id já está cadastrado.
 *
 * POR QUE ISTO EXISTE
 * "Deletar app" é soft delete: o app sai da lista mas continua ocupando o
 * `app_id`. Quem apagava e tentava recadastrar recebia só um toast vermelho
 * com "App ID X já está em uso" — verdadeiro e inútil, porque o app não
 * aparecia em lugar nenhum da tela e não havia o que fazer a respeito.
 * Aconteceu em 27/09 com os apps 4390490687763392 e 1591312979379285.
 *
 * Agora o diálogo diz QUAL app é, em que estado está, e oferece a saída:
 * reativar, quando a pessoa tem permissão para isso.
 */

interface Props {
    conflito: AppIdEmUsoDetalhe | null;
    reativando: boolean;
    onReativar: (appDbId: number) => void;
    onClose: () => void;
}

export default function AppConflitoDialog({ conflito, reativando, onReativar, onClose }: Props) {
    if (!conflito) return null;
    const { app, ativo, pode_reativar } = conflito;
    const doSistema = app.user_id === null;

    return (
        <Dialog open onOpenChange={(o) => { if (!o && !reativando) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Este app já está cadastrado
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
                        <div className="text-sm font-medium text-gray-900">{app.app_name}</div>
                        <div className="font-mono text-xs text-gray-500">ID {app.app_id}</div>
                        <div className="flex items-center gap-2 pt-1">
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ativo
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                {ativo ? 'Ativo' : 'Desativado'}
                            </span>
                            <span className="text-xs text-gray-500">
                                {doSistema ? 'App do Sistema' : 'Criado por outro cadastro'}
                            </span>
                        </div>
                    </div>

                    {ativo ? (
                        <p className="text-sm text-gray-600">
                            Ele já está ativo e aparece na aba <strong>Ativos</strong>. Não é
                            preciso cadastrar de novo — o mesmo <code className="text-xs">app_id</code>{' '}
                            não pode existir duas vezes.
                        </p>
                    ) : pode_reativar ? (
                        <p className="text-sm text-gray-600">
                            Ele foi <strong>desativado</strong>, não apagado — por isso o{' '}
                            <code className="text-xs">app_id</code> continua ocupado. Em vez de
                            cadastrar outro, dá para reativar este e recuperar o cadastro como
                            estava, com os grupos e páginas que já apontavam para ele.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600">
                            Ele foi <strong>desativado</strong> e pertence a outro cadastro, então
                            você não pode reativá-lo. Peça a um administrador para reativar, ou use
                            outro <code className="text-xs">app_id</code>.
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={reativando}>
                        Fechar
                    </Button>
                    {!ativo && pode_reativar && (
                        <Button onClick={() => onReativar(app.id)} disabled={reativando}>
                            {reativando
                                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                : <Power className="h-4 w-4 mr-2" />}
                            Reativar este app
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
